/**
 * Quest pin visit states + short hop arrow for a day's schedule.
 * Shared by the full Map tab and the compact day map.
 */
import type { MapPin, ScheduleItem, TripData } from "../types";
import { shortHopArrow } from "./hop";
import { itemVisitStates } from "./questVisit";
import { sortScheduleItems } from "./sortSchedule";
import { findPinForScheduleItem } from "../components/PlacePhoto";

export type PinVisit = "unvisited" | "active" | "visited";

export interface DayQuestState {
  pinState: Map<string, PinVisit>;
  arrow: ReturnType<typeof shortHopArrow>;
  activePin: MapPin | null;
}

const EMPTY: DayQuestState = {
  pinState: new Map(),
  arrow: null,
  activePin: null,
};

export function computeDayQuest(
  dayItems: ScheduleItem[],
  pins: TripData["pins"],
  selectedItemId: string | null | undefined,
): DayQuestState {
  const ordered = sortScheduleItems(dayItems);
  if (ordered.length === 0) return EMPTY;

  const visits = itemVisitStates(
    ordered.map((item) => item.id),
    selectedItemId,
  );
  const pinState = new Map<string, PinVisit>();
  const rank: Record<PinVisit, number> = {
    unvisited: 0,
    visited: 1,
    active: 2,
  };

  const pointFor = (index: number) => {
    const pin = findPinForScheduleItem(ordered[index], pins, pins);
    if (!pin || !Number.isFinite(pin.lat) || !Number.isFinite(pin.lng)) {
      return null;
    }
    return pin;
  };

  ordered.forEach((item, index) => {
    const pin = pointFor(index);
    if (!pin) return;
    const visit = visits.byId.get(item.id);
    const next: PinVisit =
      visit === "active"
        ? "active"
        : visit === "visited"
          ? "visited"
          : "unvisited";
    const prev = pinState.get(pin.id);
    if (!prev || rank[next] > rank[prev]) pinState.set(pin.id, next);
  });

  const activeIndex = ordered.findIndex((item) => item.id === visits.activeId);
  const activePin = activeIndex >= 0 ? pointFor(activeIndex) : null;
  let prevPin: ReturnType<typeof pointFor> = null;
  for (let i = activeIndex - 1; i >= 0; i--) {
    const pin = pointFor(i);
    if (pin) {
      prevPin = pin;
      break;
    }
  }
  const arrow =
    activePin &&
    prevPin &&
    (activePin.lat !== prevPin.lat || activePin.lng !== prevPin.lng)
      ? shortHopArrow(prevPin, activePin)
      : null;

  return { pinState, arrow, activePin };
}

export function pinsForDay(pins: MapPin[], date: string): MapPin[] {
  return pins.filter((p) => p.dayIds?.includes(date));
}
